import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "./Reveal";
import { EmSwash } from "./EmSwash";
import { PageAccent } from "./PageAccent";
import { TrustCard } from "./TrustCard";

/**
 * GeneralFundBand — the "or, give to the fund" moment on /demo.
 *
 * Sits between EndingSoon (urgency campaigns) and the closing DoodleDivider
 * on the donor demo. Gives the browsing donor a second, equally clear
 * path: skip picking one student, and give to the My Rich Aunt Fund
 * — from which we route the gift to students who need help most.
 *
 * The primary CTA opens `<DonateDialog>` without campaign context, which
 * already renders as "Give to the My Rich Aunt Fund" (no campaign prop).
 *
 * Right-column content (2026-07-19e): the trio of reason cards was
 * replaced with the shared `<TrustCard />` — the 4-point 100% Verified
 * card that also lives next to Terri's Footer signature elsewhere. Founder
 * note: this slot is a placeholder — eventually it should become an
 * expression of gratitude for the donor, or route them into an
 * "aunties + uncles" offering path (e.g. posting internship opportunities).
 *
 * Props:
 *   onDonate — click handler; expected to open <DonateDialog> in
 *              general-fund mode (no `raised`/`studentName` props).
 */

export const GeneralFundBand = ({ onDonate }) => {
  return (
    <section
      id="general-fund"
      data-testid="general-fund-band"
      className="relative"
    >
      {/* Warm tangerine wash — subtle, keeps the band distinct from the
          white-sand campaign sections above/below without shouting. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-tangerine/[0.05]"
      />

      <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-36">
        <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12 lg:gap-x-12">
          {/* Left column — pitch + primary CTA */}
          <div className="lg:col-span-6">
            <FadeUp tier="sm">
              <PageAccent className="mb-5" />
            </FadeUp>

            <FadeUp>
              <span
                data-testid="general-fund-eyebrow"
                className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine"
              >
                Or — give to the fund
              </span>
              <h2
                data-testid="general-fund-heading"
                className="mt-4 max-w-xl font-serif text-3xl lg:text-[3.5rem] leading-[1.02] tracking-tight text-ink"
              >
                Don't have a{" "}
                <EmSwash color="text-grass">preference?</EmSwash>
              </h2>
              <p
                data-testid="general-fund-body"
                className="mt-6 max-w-lg text-base leading-relaxed text-olive"
              >
                Give to the general fund. My Rich Aunt pools your donation with those from other donors and sends it to the students who need help most.
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <button
                type="button"
                data-testid="general-fund-donate-button"
                onClick={onDonate}
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-4 text-base font-semibold text-sand transition-colors hover:bg-forest"
              >
                Donate to the General Fund
                <ArrowUpRight
                  className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  strokeWidth={2}
                />
              </button>
              <p className="mt-4 max-w-md text-[13px] leading-relaxed text-olive/80">
                Or scroll on to review a specific student campaign — both
                paths support the same mission.
              </p>
            </FadeUp>
          </div>

          {/* Right column — TrustCard placeholder.
              Founder note: eventually swap for an expression of gratitude
              for the donor, or a next-step ask (e.g. "aunties + uncles"
              posting internship opportunities). */}
          <div className="lg:col-span-5 lg:col-start-8">
            <FadeUp>
              <TrustCard data-testid="general-fund-trust-card" />
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};
